import React, { useEffect, useState } from 'react'
import './white-labels-list.scss'
import classnames from 'classnames'
import clone from 'clone-deep'
import * as deepEqual from 'react-fast-compare'
import DataTableView from '../../components/data-table-view/data-table-view'
import { useMountedRef } from '../../hooks/use-mounted-ref'
import { fmtDateString, fmtNumberAsDelineatedCount } from '../../helpers/formatting'
import { useAxiosCancelToken } from '../../hooks/use-axios-cancel-token'
import EntityDisplay, { EntityTitle } from '../../components/data-table-view/entity-display'
import { useService } from '@halo/hooks/use-service'
import { AppState } from '@halo/stores/app.state'
import { createWhiteLabel, fetchWhiteLabels, patchWhiteLabel } from '@halo/api/white-labels.api'
import { DeleteFilled, EditOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import { Icon as LegacyIcon } from '@ant-design/compatible'
import { Button, Dropdown, Menu, notification, Table } from 'antd'
import { stripUndefined } from '@halo/helpers/clean-objects'
import { collateConfig, buildApiOrderConfig } from '@halo/components/data-table-view/helpers'
import { useRefState } from '@halo/hooks/use-ref-state'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { DataTableContext } from '@halo/components/data-table-view/context'
import { DataSourceResolver } from '@halo/components/data-table-view/types'
import { observer } from 'mobx-react'
import WhiteLabelModel from '@halo/models/white-label.model'
import { StatusBadge } from '@halo/components/badges/status-badge'
import { StatusName } from '@pushly/aquarium-utils/lib/enums'
import randomstring from 'randomstring'
import { WHITE_LABELS_LIST_ACTION } from '@halo/components/white-labels-list/enums'
import {
    handleWhiteLabelDestroy,
    handleWhiteLabelDisable,
    handleWhiteLabelEnable,
} from '@halo/components/white-labels-list/helpers'
import WhiteLabelEditor from '@halo/components/white-labels-list/white-label-editor'
import { TablePaginationConfig } from 'antd/lib/table/interface'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

const DateTimeView = observer(({ date }: { date: string }) => {
    const appState = useService<AppState>(AppState)

    return !date ? (
        <>--</>
    ) : (
        <EntityDisplay
            top={{ left: <span className="dt-view-date">{fmtDateString(date, 'll')}</span> }}
            mid={{
                left: (
                    <span className="dt-view-time">
                        {fmtDateString(date, 'LT z', appState.displayTimeZone ?? 'utc')}
                    </span>
                ),
            }}
        />
    )
})

const renderTotal = (total: number, config: any): React.ReactNode => {
    const {
        pagination: { current, pageSize },
    } = clone(config)
    const start = fmtNumberAsDelineatedCount((current - 1) * pageSize + 1)
    const end = fmtNumberAsDelineatedCount(current * pageSize)
    const ttl = fmtNumberAsDelineatedCount(total)

    return total <= pageSize
        ? `Showing ${ttl} of ${ttl} Total White-Label(s)`
        : `Showing ${start}-${end} of ${ttl} Total White-Label(s)`
}

const WhiteLabelsList = () => {
    const appState = useService<AppState>(AppState)

    const [_mounted, runIfMounted] = useMountedRef()
    const [editorState, setEditorState] = useState<{
        visible: boolean
        key?: string
        whiteLabel?: WhiteLabelModel
    }>({ visible: false })

    /**
     * Data View configuration & state manager
     *
     * @param loading
     * @param data - current page data
     * @param pagination - AntD pagination config
     * @param filters - AntD filters config + { tableSearchValue: string }
     * @param sortOrder - AntD sortOrder config
     */
    // @ts-ignore
    const [whiteLabels, setWhiteLabels] = useRefState({
        loading: false,
        data: undefined,
        pagination: {
            pageSize: 15,
            current: 1,
            total: 15,

            size: 'small',
            showSizeChanger: false,
            showQuickJumper: true,
            // @ts-ignore
            showTotal: (total: number) => renderTotal(total, whiteLabels.current),
            itemRender: (current: any, type: string, originalElement: any) => {
                if (type === 'prev') {
                    return <a>Previous</a>
                }
                if (type === 'next') {
                    return <a>Next</a>
                }
                if (type === 'page' || /^jump/.test(type)) {
                    return ''
                }

                return originalElement
            },
        } as TablePaginationConfig,
        filters: {},
        sortOrder: {
            columnKey: 'whitelabel.created_at',
            field: 'created_at',
            order: 'descend',
        },
    })

    const [paginationLinks, setPaginationLinks] = React.useState<null | IApiResponsePaginationLinks>(null)
    const [cancelToken, resetCancelToken] = useAxiosCancelToken()

    /**
     * DataSourceResolver
     * Pushly extension for AntD table change event
     * accepts undefined values allowing inheritance
     *
     * @param [pagination] - AntD pagination config
     * @param [filters] - AntD filters config + { tableSearchValue: string }
     * @param [sortOrder] - AntD sortOrder config
     * @param [extra] - AntD sortOrder config
     */
    const fetchData: DataSourceResolver<any> = async (pagination, filters, sortOrder, extra): Promise<void> => {
        setWhiteLabels({ ...whiteLabels.current, loading: true })
        let data: any[] = []

        let paginationConfig = clone(collateConfig(pagination, whiteLabels.current.pagination))
        let filtersConfig = clone(collateConfig(filters, whiteLabels.current.filters))
        let sortConfig = clone(collateConfig(sortOrder, whiteLabels.current.sortOrder))

        const pageSizeChanged = paginationConfig.pageSize !== whiteLabels.current.pagination?.pageSize
        const filtersChanged = !deepEqual(filtersConfig, whiteLabels.current.filters)
        const sortChanged = !deepEqual(sortConfig, whiteLabels.current.sortOrder)
        const changesDetected = pageSizeChanged || filtersChanged || sortChanged

        // reset current page on filter/sort/size change
        if (changesDetected) {
            paginationConfig.current = 1
        }

        const searchVal = filtersConfig.tableSearchValue?.trim()
        const orderConfig = buildApiOrderConfig(sortConfig)

        const query = stripUndefined({
            limit: paginationConfig?.pageSize,
            page: paginationConfig?.current,
            search: !searchVal ? undefined : searchVal,
            status: !filtersConfig.status?.length ? undefined : filtersConfig.status.join(','),
            target_name: !filtersConfig.target_name?.length ? undefined : filtersConfig.target_name.join(','),
            order: !orderConfig ? undefined : JSON.stringify(orderConfig),
        })

        resetCancelToken()
        const res = await fetchWhiteLabels({
            query,
            showLoadingScreen: false,
            cancelToken: cancelToken.current,
        })

        if (res && res.ok) {
            data = res.data?.map(WhiteLabelModel.build) ?? []
            setPaginationLinks(res.meta.links ?? paginationLinks)
        }

        runIfMounted(() => {
            setWhiteLabels({
                loading: false,
                data,
                pagination: paginationConfig,
                filters: filtersConfig,
                sortOrder: sortConfig,
            })
        })
    }

    const handleRowAction = (action: string | number, whiteLabel: WhiteLabelModel) => {
        if (action === WHITE_LABELS_LIST_ACTION.EDIT) {
            setEditorState({
                key: randomstring.generate(),
                visible: true,
                whiteLabel,
            })
        } else if (action === WHITE_LABELS_LIST_ACTION.ENABLE) {
            handleWhiteLabelEnable(whiteLabel, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === WHITE_LABELS_LIST_ACTION.DISABLE) {
            handleWhiteLabelDisable(whiteLabel, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === WHITE_LABELS_LIST_ACTION.DESTROY) {
            handleWhiteLabelDestroy(whiteLabel, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        }
    }

    /**
     * Initial data load runs once on first mount/render
     */
    useEffect(() => {
        fetchData()
    }, [false])

    return (
        <div className={classnames('white-labels-list')}>
            <DataTableContext.Provider
                value={{
                    loading: whiteLabels.current.loading,
                    currentDataSource: whiteLabels.current.data,
                    resolveDataSource: fetchData,
                }}
            >
                <DataTableView
                    searchPlaceholder="Search display name, hostname, or ID"
                    actions={[
                        <Button
                            key="new-white-label"
                            size="small"
                            type="primary"
                            onClick={() => {
                                setEditorState({
                                    key: randomstring.generate(),
                                    visible: true,
                                })
                            }}
                        >
                            <span>New White-Label</span>
                        </Button>,
                    ]}
                >
                    <Table
                        size="small"
                        bordered={false}
                        rowKey="id"
                        pagination={false}
                        footer={(currentTableData) => (
                            <SwSimpleLinkPagination
                                currentTableData={currentTableData}
                                links={paginationLinks}
                                onPrev={(_ev, _link, config) => {
                                    setWhiteLabels({
                                        ...whiteLabels.current,
                                        pagination: {
                                            ...whiteLabels.current.pagination,
                                            current: config.current!,
                                        },
                                    })
                                    fetchData()
                                }}
                                onNext={(_ev, _link, config) => {
                                    setWhiteLabels({
                                        ...whiteLabels.current,
                                        pagination: {
                                            ...whiteLabels.current.pagination,
                                            current: config.current!,
                                        },
                                    })
                                    fetchData()
                                }}
                            />
                        )}
                        locale={{
                            emptyText: <AntdTableEmptyPlaceholder text="No White-Label(s)" icon="tag" />,
                        }}
                        loading={
                            !whiteLabels.current.loading
                                ? false
                                : {
                                      indicator: <Loading3QuartersOutlined spin={true} />,
                                  }
                        }
                        dataSource={whiteLabels.current.data}
                        onChange={fetchData}
                    >
                        <Table.Column
                            title="Name"
                            className="hostname"
                            dataIndex="displayName"
                            render={(displayName, whiteLabel: WhiteLabelModel) => {
                                const previewDomain = appState.currentUserDomains?.find(
                                    (d) => d.id.toString() === whiteLabel.getPreviewDomainId().toString(),
                                )

                                return (
                                    <EntityDisplay
                                        top={{
                                            left: <EntityTitle>{displayName}</EntityTitle>,
                                            right: (
                                                <StatusBadge
                                                    type="generic"
                                                    expanded={true}
                                                    status={
                                                        whiteLabel.getIsActive()
                                                            ? StatusName.ACTIVE
                                                            : StatusName.DISABLED
                                                    }
                                                />
                                            ),
                                        }}
                                        mid={{
                                            left: <span className="hostname">{whiteLabel.getHostname()}</span>,
                                            right: <span>Previews on {previewDomain?.display_name}</span>,
                                        }}
                                    />
                                )
                            }}
                        />
                        <Table.Column
                            className="created_at"
                            title="Created"
                            dataIndex="createdAt"
                            align="center"
                            render={(dt) => <DateTimeView date={dt} />}
                        />
                        <Table.Column
                            className="actions"
                            title="Actions"
                            dataIndex="actions"
                            align="center"
                            render={(_, whiteLabel: WhiteLabelModel) => {
                                const isActive = whiteLabel.getIsActive()

                                return (
                                    <Dropdown.Button
                                        size="small"
                                        className="white-label-row-actions-btn"
                                        onClick={() => handleRowAction(WHITE_LABELS_LIST_ACTION.EDIT, whiteLabel)}
                                        overlay={
                                            <Menu
                                                className="white-label-row-actions-dropdown-menu"
                                                onClick={({ key }) => handleRowAction(key, whiteLabel)}
                                            >
                                                <Menu.Item
                                                    key={
                                                        isActive
                                                            ? WHITE_LABELS_LIST_ACTION.DISABLE
                                                            : WHITE_LABELS_LIST_ACTION.ENABLE
                                                    }
                                                >
                                                    <LegacyIcon
                                                        type={isActive ? 'pause-circle' : 'play-circle'}
                                                        theme="filled"
                                                    />
                                                    <span>{isActive ? 'Disable' : 'Enable'}</span>
                                                </Menu.Item>
                                                <Menu.Divider />
                                                <Menu.Item key={WHITE_LABELS_LIST_ACTION.DESTROY}>
                                                    <DeleteFilled />
                                                    <span>Delete</span>
                                                </Menu.Item>
                                            </Menu>
                                        }
                                    >
                                        <EditOutlined />
                                        <span>Edit</span>
                                    </Dropdown.Button>
                                )
                            }}
                        />
                    </Table>
                </DataTableView>
            </DataTableContext.Provider>

            <WhiteLabelEditor
                key={editorState.key}
                visible={editorState.visible}
                mode={!editorState.whiteLabel ? 'create' : 'edit'}
                whiteLabel={editorState.whiteLabel}
                onClose={() => setEditorState({ visible: false })}
                onSubmit={async (whiteLabel) => {
                    notification.destroy()

                    const isCreateMode = !editorState.whiteLabel

                    let res: any
                    if (isCreateMode) {
                        res = await createWhiteLabel(whiteLabel)
                    } else {
                        res = await patchWhiteLabel(editorState.whiteLabel.getId(), whiteLabel)
                    }

                    if (res.ok) {
                        notification.success({
                            message: '',
                            description: isCreateMode
                                ? 'White-Label successfully created.'
                                : 'White-Label successfully updated.',
                        })

                        setEditorState({ visible: false })
                        return fetchData()
                    } else {
                        notification.error({
                            message: '',
                            description:
                                res.error?.response?.data?.message ??
                                res.error?.message ??
                                'An unknown error has occurred.',
                        })
                    }
                }}
            />
        </div>
    )
}

export default WhiteLabelsList
